import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Fingerprint, ArrowLeft, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

// Helper functions for base64url encoding/decoding
function base64URLDecode(str: string): ArrayBuffer {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
}

function arrayBufferToBase64URL(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

interface PasskeyLoginProps {
  onSuccess: () => void;
  onBack: () => void;
  onNoPasskey: () => void;
}

export function PasskeyLogin({ onSuccess, onBack, onNoPasskey }: PasskeyLoginProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isInIframe = typeof window !== "undefined" && window.self !== window.top;

  const handlePasskeyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Step 1: Start authentication - get challenge from server
      const { data: startData, error: startError } = await supabase.functions.invoke(
        "passkey-authenticate",
        {
          body: { action: "start", email },
        }
      );

      if (startError) {
        throw new Error(startError.message);
      }

      if (!startData.hasPasskeys) {
        onNoPasskey();
        return;
      }

      // Step 2: Get the credential from the browser/device using native WebAuthn API
      const options = startData.options;
      
      // Convert base64url to ArrayBuffer for credentials
      const allowCredentials = options.allowCredentials?.map((cred: { id: string; type: "public-key"; transports?: AuthenticatorTransport[] }) => ({
        id: base64URLDecode(cred.id),
        type: cred.type as PublicKeyCredentialType,
        transports: cred.transports as AuthenticatorTransport[],
      }));

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: base64URLDecode(options.challenge),
        rpId: options.rpId,
        allowCredentials,
        userVerification: options.userVerification as UserVerificationRequirement,
        timeout: options.timeout,
      };

      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error("Keine Anmeldedaten erhalten");
      }

      const response = credential.response as AuthenticatorAssertionResponse;

      // Step 3: Verify with server
      const { data: finishData, error: finishError } = await supabase.functions.invoke(
        "passkey-authenticate",
        {
          body: {
            action: "finish",
            credential: {
              id: credential.id,
              rawId: arrayBufferToBase64URL(credential.rawId),
              type: credential.type,
              response: {
                clientDataJSON: arrayBufferToBase64URL(response.clientDataJSON),
                authenticatorData: arrayBufferToBase64URL(response.authenticatorData),
                signature: arrayBufferToBase64URL(response.signature),
                userHandle: response.userHandle ? arrayBufferToBase64URL(response.userHandle) : null,
              },
            },
            userId: startData.userId,
          },
        }
      );

      if (finishError) {
        throw new Error(finishError.message);
      }

      if (!finishData.success) {
        throw new Error(finishData.error || "Authentifizierung fehlgeschlagen");
      }

      // Step 4: Create Supabase session via magiclink OTP token returned by backend
      const tokenHash = finishData.token_hash || finishData.hashed_token;
      if (!tokenHash) {
        throw new Error("Session-Erstellung fehlgeschlagen");
      }

      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: "magiclink",
      });

      if (otpError) {
        throw new Error(otpError.message);
      }

      toast.success("Erfolgreich mit Passkey angemeldet!");
      onSuccess();

    } catch (err: unknown) {
      console.error("Passkey login error:", err);
      
      if (err instanceof Error) {
        if (err.name === "NotAllowedError") {
          const iframeBlocked = isInIframe || err.message.toLowerCase().includes("ancestors");
          if (iframeBlocked) {
            setError(
              "Passkey-Anmeldung ist in der Vorschau (Iframe) aus Sicherheitsgründen blockiert. Bitte öffnen Sie die Seite in einem neuen Tab (ohne Editor-Iframe) und versuchen Sie es erneut."
            );
          } else {
            setError(
              "Authentifizierung abgebrochen oder nicht erlaubt. Hinweis: Passkeys sind an die Domain gebunden – bitte auf derselben Domain anmelden, auf der der Passkey eingerichtet wurde."
            );
          }
        } else if (err.name === "NotSupportedError") {
          setError("Passkeys werden auf diesem Gerät nicht unterstützt.");
        } else if (err.message.includes("No passkeys")) {
          onNoPasskey();
        } else {
          setError(err.message || "Fehler bei der Passkey-Authentifizierung");
        }
      } else {
        setError("Unbekannter Fehler");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl flex items-center justify-center gap-2">
            <Fingerprint className="h-6 w-6" />
            Mit Passkey anmelden
          </CardTitle>
          <CardDescription>
            Nutzen Sie Face ID, Touch ID oder Fingerabdruck
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handlePasskeyLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="passkey-email">E-Mail</Label>
              <Input
                id="passkey-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                required
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading || !email}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Warte auf Biometrie...
                </>
              ) : (
                <>
                  <Fingerprint className="mr-2 h-4 w-4" />
                  Mit Passkey anmelden
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück zur Passwort-Anmeldung
            </Button>

            {isInIframe && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => window.open(window.location.href, "_blank", "noopener,noreferrer")}
              >
                In neuem Tab öffnen
              </Button>
            )}
          </form>

          <p className="text-xs text-muted-foreground text-center">
            Passkeys nutzen biometrische Daten Ihres Geräts für eine sichere Anmeldung ohne Passwort.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
